/**
 * Aggregation API module
 * Requirements: 16.1, 16.2, 16.3, 16.4, 16.7 - Aggregation data retrieval, export, and network graph
 */
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';
import type {
  AggregationResult,
  AggregationScope,
  DepartmentOption,
  ExportFormat,
  NetworkGraphData,
  NetworkGraphJobStatusResponse,
  ProcessingMode,
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

/**
 * Get aggregation data for department or company
 */
async function getAggregation(
  scope: AggregationScope,
  targetId: string,
  startDate?: Date,
  endDate?: Date
): Promise<AggregationResult> {
  const params: Record<string, string> = {
    scope,
    targetId,
  };

  if (startDate) {
    params.startDate = startDate.toISOString();
  }
  if (endDate) {
    params.endDate = endDate.toISOString();
  }

  const client = getApiClient();
  const response = await client.get<{ aggregation: AggregationResult }>(
    '/aggregation',
    { params }
  );
  return response.data.aggregation;
}

/**
 * Get department list for selection
 */
async function getDepartments(): Promise<DepartmentOption[]> {
  const client = getApiClient();
  const response = await client.get<{ departments: DepartmentOption[] }>(
    '/departments'
  );
  return response.data.departments;
}

/**
 * Export aggregation data
 */
async function exportAggregation(
  scope: AggregationScope,
  targetId: string,
  format: ExportFormat,
  startDate?: Date,
  endDate?: Date
): Promise<string> {
  const params: Record<string, string> = {
    scope,
    targetId,
    format,
  };

  if (startDate) {
    params.startDate = startDate.toISOString();
  }
  if (endDate) {
    params.endDate = endDate.toISOString();
  }

  const client = getApiClient();
  const response = await client.get<{ downloadUrl: string }>(
    '/aggregation/export',
    { params }
  );
  return response.data.downloadUrl;
}

/**
 * Get network graph data
 * Requirements: 16.3, 16.4 - Network graph visualization
 */
async function getNetworkGraph(
  scope: AggregationScope,
  targetId: string,
  processingMode: ProcessingMode = 'realtime'
): Promise<NetworkGraphData> {
  const params: Record<string, string> = {
    scope,
    targetId,
    processingMode,
  };

  const client = getApiClient();
  const response = await client.get<{ networkGraph: NetworkGraphData }>(
    '/aggregation/network-graph',
    { params }
  );
  return response.data.networkGraph;
}

/**
 * Start async network graph job
 * Requirements: 16.3, 16.4 - Network graph async processing for large datasets
 */
async function startNetworkGraphJob(
  scope: AggregationScope,
  targetId: string
): Promise<string> {
  const client = getApiClient();
  const response = await client.post<{ jobId: string }>(
    '/aggregation/network-graph/jobs',
    { scope, targetId }
  );
  return response.data.jobId;
}

/**
 * Get network graph job status
 * Requirements: 16.3, 16.4 - Network graph async job polling
 */
async function getNetworkGraphJobStatus(
  jobId: string
): Promise<NetworkGraphJobStatusResponse> {
  const client = getApiClient();
  const response = await client.get<NetworkGraphJobStatusResponse>(
    `/aggregation/network-graph/jobs/${jobId}`
  );
  return response.data;
}

/**
 * Get employee count for network graph
 * Requirements: 16.5, 16.6 - Check count before generating network graph
 */
async function getNetworkGraphEmployeeCount(
  scope: AggregationScope,
  targetId: string
): Promise<{ count: number; canRealtime: boolean; requiresAsync: boolean; exceedsLimit: boolean }> {
  const client = getApiClient();
  const response = await client.get<{
    count: number;
    canRealtime: boolean;
    requiresAsync: boolean;
    exceedsLimit: boolean;
  }>('/aggregation/network-graph/count', {
    params: { scope, targetId },
  });
  return response.data;
}

export const aggregationApi = {
  getAggregation,
  getDepartments,
  exportAggregation,
  getNetworkGraph,
  startNetworkGraphJob,
  getNetworkGraphJobStatus,
  getNetworkGraphEmployeeCount,
};
